import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import apiService from '../../services/api'

// Async thunks for authentication
export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await apiService.post('/auth/register/', userData)
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue, dispatch }) => {
    try {
      const response = await apiService.post('/auth/login/', credentials)
      // After successful login, fetch the enhanced profile with subscription data
      const profileResponse = await apiService.get('/auth/enhanced-profile/')
      return {
        ...response,
        user: profileResponse.user || profileResponse
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.post('/auth/simple-logout/', {})
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const fetchUserProfile = createAsyncThunk(
  'auth/profile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.get('/auth/enhanced-profile/')
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const checkAuthStatus = createAsyncThunk(
  'auth/status',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.get('/auth/status/')
      // If authenticated, fetch the enhanced profile to get subscription data
      if (response.authenticated) {
        const profileResponse = await apiService.get('/auth/enhanced-profile/')
        return {
          ...response,
          user: profileResponse.user || profileResponse
        }
      }
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const fetchCSRFToken = createAsyncThunk(
  'auth/csrf-token',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.get('/auth/csrf-token/')
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

const initialState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  csrfToken: null,
  authStatus: 'idle', // 'idle' | 'checking' | 'authenticated' | 'unauthenticated'
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearAuthData: (state) => {
      state.user = null
      state.isAuthenticated = false
      state.authStatus = 'unauthenticated'
      state.error = null
    },
    setAuthStatus: (state, action) => {
      state.authStatus = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      // Register user
      .addCase(registerUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload.user
        state.isAuthenticated = true
        state.authStatus = 'authenticated'
        state.error = null
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.isAuthenticated = false
        state.authStatus = 'unauthenticated'
      })
      
      // Login user
      .addCase(loginUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload.user
        state.isAuthenticated = true
        state.authStatus = 'authenticated'
        state.error = null
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.isAuthenticated = false
        state.authStatus = 'unauthenticated'
      })
      
      // Logout user
      .addCase(logoutUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false
        state.user = null
        state.isAuthenticated = false
        state.authStatus = 'unauthenticated'
        state.error = null
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Fetch user profile
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload.user || action.payload
        state.isAuthenticated = true
        state.authStatus = 'authenticated'
        state.error = null
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.isAuthenticated = false
        state.authStatus = 'unauthenticated'
      })
      
      // Check auth status
      .addCase(checkAuthStatus.pending, (state) => {
        state.authStatus = 'checking'
        state.error = null
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        if (action.payload.authenticated) {
          state.isAuthenticated = true
          state.authStatus = 'authenticated'
          state.user = action.payload.user
        } else {
          state.isAuthenticated = false
          state.authStatus = 'unauthenticated'
          state.user = null
        }
        state.error = null
      })
      .addCase(checkAuthStatus.rejected, (state, action) => {
        state.isAuthenticated = false
        state.authStatus = 'unauthenticated'
        state.user = null
        state.error = action.payload
      })
      
      // Fetch CSRF token
      .addCase(fetchCSRFToken.pending, (state) => {
        state.error = null
      })
      .addCase(fetchCSRFToken.fulfilled, (state, action) => {
        state.csrfToken = action.payload.csrf_token || action.payload.token
        state.error = null
      })
      .addCase(fetchCSRFToken.rejected, (state, action) => {
        state.error = action.payload
      })
  }
})

export const { clearError, clearAuthData, setAuthStatus } = authSlice.actions

export default authSlice.reducer

// Selectors
export const selectAuth = (state) => state.auth
export const selectUser = (state) => state.auth.user
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated
export const selectAuthLoading = (state) => state.auth.loading
export const selectAuthError = (state) => state.auth.error
export const selectAuthStatus = (state) => state.auth.authStatus
export const selectCSRFToken = (state) => state.auth.csrfToken